import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { useSupabase } from '../contexts/SupabaseContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { 
  MessageCircle, 
  X, 
  Users, 
  Search, 
  UserPlus, 
  CheckCircle, 
  Send,
  ArrowLeft,
  Bell,
  Smile,
  Paperclip,
  Image,
  FileText,
  Film,
  Music,
  Mic,
  Square,
  Trash2,
  Phone,
  Video,
  Coins
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { 
  ensureUserExists, 
  getExcludedUserIds, 
  uploadChatFile, 
  sendMessageWithFile,
  blockUser,
  unblockUser,
  isUserBlocked,
  getUnreadMessageCount,
  subscribeToAllUserMessages,
  unsubscribeChannel,
  getUnreadCountsPerFriend,
  subscribeToFriendsList,
  getAllFriendRequests,
  resetFriendRequest,
  initiateCall,
  respondToCall,
  cancelCall,
  subscribeToIncomingCalls,
  subscribeToCallStatus,
  getAllUserConversations,
  createDirectContact,
  getConversationContactType,
  deleteMessage,
  deleteAllMessages
} from '../services/supabaseFriendChat';
import EmojiPicker from 'emoji-picker-react';
import { GiphyFetch } from '@giphy/js-fetch-api';
import { Grid } from '@giphy/react-components';
import VideoCallModal from './VideoCallModal';
import IncomingCallModal from './IncomingCallModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Initialize Giphy
const gf = new GiphyFetch(process.env.REACT_APP_GIPHY_API_KEY || '');

// Custom voice message player component
const VoiceMessagePlayer = ({ src, isOwn }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = React.useRef(null);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  const bars = [3, 5, 8, 6, 10, 7, 9, 5, 7, 4, 8, 6, 9, 5, 7, 3];

  return (
    <div className={`flex items-center gap-2 px-1 py-0.5 min-w-[160px]`}>
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={e => setProgress(e.target.currentTime / (e.target.duration || 1))}
        onLoadedMetadata={e => setDuration(e.target.duration)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
      />
      <button
        onClick={toggle}
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-transform active:scale-90 ${
          isOwn ? 'bg-white/20 hover:bg-white/30' : 'bg-amber-500 hover:bg-amber-600'
        }`}
      >
        {playing
          ? <svg className={`w-3.5 h-3.5 ${isOwn ? 'text-white' : 'text-white'}`} fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
          : <svg className={`w-3.5 h-3.5 ml-0.5 ${isOwn ? 'text-white' : 'text-white'}`} fill="currentColor" viewBox="0 0 24 24"><polygon points="5,3 19,12 5,21"/></svg>
        }
      </button>
      {/* Waveform bars */}
      <div className="flex items-center gap-[2px] flex-1">
        {bars.map((h, i) => {
          const filled = progress > i / bars.length;
          return (
            <div
              key={i}
              className={`rounded-full transition-all ${
                playing && filled
                  ? (isOwn ? 'bg-white animate-pulse' : 'bg-amber-500 animate-pulse')
                  : filled
                  ? (isOwn ? 'bg-white/80' : 'bg-amber-400')
                  : (isOwn ? 'bg-white/30' : 'bg-gray-300')
              }`}
              style={{ width: 2.5, height: h, animationDelay: `${i * 40}ms` }}
            />
          );
        })}
      </div>
      <span className={`text-xs flex-shrink-0 font-mono ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>
        {playing
          ? fmt((audioRef.current?.currentTime) || 0)
          : fmt(duration)}
      </span>
    </div>
  );
};

const FloatingChatWidget = () => {
  const { user, openAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    sendFriendRequest,
    fetchFriendRequests,
    acceptFriendRequest,
    rejectFriendRequest,
    fetchFriends,
    openConversation,
    sendMessage: sendMessageToSupabase,
    fetchMessages,
    markAsRead,
    subscribeToConversation,
    subscribeToUserFriendRequests,
    unsubscribe,
    pendingRequests,
    friends: supabaseFriends
  } = useSupabase();

  // Admin/Subadmin emails (portal support team)
  const ADMIN_EMAILS = ['shifracs@gmail.com', 'Advisor@mednerd.in'];
  const isAdminOrSubadmin = ADMIN_EMAILS.includes(user?.email);

  // Widget state
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('friends'); // friends, requests, discover
  const [activeChat, setActiveChat] = useState(null);

  // Dispatch open/close events for BottomNav
  useEffect(() => {
    window.dispatchEvent(new CustomEvent(isOpen ? 'openFriendsChat' : 'closeFriendsChat'));
  }, [isOpen]);

  // Lock ALL scroll on the page behind when chat is open.
  // Only scroll events that originate inside the chat panel are allowed through.
  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = 'hidden';

    const blockScroll = (e) => {
      const panel = document.getElementById('chat-widget-panel');
      if (panel && panel.contains(e.target)) return; // allow inside panel
      e.preventDefault();
    };

    document.addEventListener('wheel', blockScroll, { passive: false });
    document.addEventListener('touchmove', blockScroll, { passive: false });

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('wheel', blockScroll);
      document.removeEventListener('touchmove', blockScroll);
    };
  }, [isOpen]);
  
  // Data state
  const [friends, setFriends] = useState([]);
  const [directContacts, setDirectContacts] = useState([]); // Conversations from employers (non-friends)
  const [friendRequests, setFriendRequests] = useState([]);
  const [allRequests, setAllRequests] = useState({ received: [], sent: [] });
  const [showAllRequests, setShowAllRequests] = useState(false);
  const [discoveredUsers, setDiscoveredUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingRequest, setSendingRequest] = useState({});
  
  // Emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);
  
  // GIF picker state
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifSearchQuery, setGifSearchQuery] = useState('');
  const gifPickerRef = useRef(null);
  
  // Messages container ref for auto-scroll
  const messagesEndRef = useRef(null);
  
  // File attachment state
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  
  // Hide when permission modal is showing
  const [permsShowing, setPermsShowing] = useState(false);
  useEffect(() => {
    const onShow = () => setPermsShowing(true);
    const onHide = () => setPermsShowing(false);
    window.addEventListener('nn-request-permissions', onShow);
    window.addEventListener('nn-permissions-closed', onHide);
    return () => {
      window.removeEventListener('nn-request-permissions', onShow);
      window.removeEventListener('nn-permissions-closed', onHide);
    };
  }, []);

  // Blocking state
  const [isBlocked, setIsBlocked] = useState(false);
  const [showBlockMenu, setShowBlockMenu] = useState(false);
  const [deletingMsgId, setDeletingMsgId] = useState(null);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isChatWithSupportTeam, setIsChatWithSupportTeam] = useState(false);
  
  // Unread messages state
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadPerFriend, setUnreadPerFriend] = useState({});
  
  // Draggable bubble state
  const [bubblePosition, setBubblePosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);
  const bubbleRef = useRef(null);
  
  // Ask Nerd AI bubble state (second bubble)
  const [nerdAiBubblePosition, setNerdAiBubblePosition] = useState({ x: null, y: null });
  const [isNerdAiDragging, setIsNerdAiDragging] = useState(false);
  const [nerdAiDragStart, setNerdAiDragStart] = useState({ x: 0, y: 0 });
  const [nerdAiHasMoved, setNerdAiHasMoved] = useState(false);
  const nerdAiBubbleRef = useRef(null);
  
  // Mobile detection - reduce bubble size by 20% on mobile
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Video/Voice Call state (Daily.co)
  const [showCallModal, setShowCallModal] = useState(false);
  const [callRoomId, setCallRoomId] = useState('');
  const [isVideoCall, setIsVideoCall] = useState(true);
  const [currentCallId, setCurrentCallId] = useState(null);
  
  // Incoming call state
  const [incomingCall, setIncomingCall] = useState(null);
  const [showIncomingCall, setShowIncomingCall] = useState(false);
  const callStatusChannelRef = useRef(null);
  
  // Bubble sizes - 20% smaller on mobile
  const friendsBubbleSize = isMobile ? 66 : 82; // 82 * 0.8 = 65.6
  const nerdAiBubbleSize = isMobile ? 54 : 68; // 68 * 0.8 = 54.4
  const bubbleGap = isMobile ? 6 : 7; // Reduced by 40% (was 10/12, now 6/7)
  
  // Default position (bottom-right with padding from edges)
  const defaultPosition = { 
    x: typeof window !== 'undefined' ? window.innerWidth - (isMobile ? 80 : 100) : 0, 
    y: typeof window !== 'undefined' ? window.innerHeight - (isMobile ? 80 : 100) : 0 
  };
  
  // Default position for Nerd AI bubble — centered above Friends bubble with gap
  // Friends bubble: left = innerWidth-padding, width=friendsBubbleSize, center = innerWidth-(padding-friendsBubbleSize/2)
  // Nerd AI bubble: width=nerdAiBubbleSize, so left = center - nerdAiBubbleSize/2
  const nerdAiDefaultPosition = { 
    x: typeof window !== 'undefined' ? window.innerWidth - (isMobile ? 80 : 100) + (friendsBubbleSize - nerdAiBubbleSize) / 2 : 0, 
    y: typeof window !== 'undefined' ? window.innerHeight - (isMobile ? 80 : 100) - nerdAiBubbleSize - bubbleGap : 0
  };

  // Initialize bubble position
  useEffect(() => {
    if (bubblePosition.x === null) {
      setBubblePosition(defaultPosition);
    }
    if (nerdAiBubblePosition.x === null) {
      setNerdAiBubblePosition(nerdAiDefaultPosition);
    }
  }, []);

  // Handle browser back button to close chat widget
  useEffect(() => {
    const handlePopState = (event) => {
      if (isOpen) {
        // Prevent default back navigation and close the widget instead
        event.preventDefault();
        setIsOpen(false);
        setActiveChat(null);
      }
    };

    // When chat opens, push a state to history
    if (isOpen) {
      window.history.pushState({ chatOpen: true }, '', window.location.href);
    }

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen]);

  // Listen for bottom nav Friends button click
  useEffect(() => {
    const handleOpenFriendsChat = () => {
      setIsOpen(true);
      setActiveChat(null);
      setActiveTab('friends');
    };

    window.addEventListener('openFriendsChat', handleOpenFriendsChat);
    
    return () => {
      window.removeEventListener('openFriendsChat', handleOpenFriendsChat);
    };
  }, []);


  // Listen for direct chat open events (from Employer Dashboard contact)
  useEffect(() => {
    const handleOpenDirectChat = async (event) => {
      const { conversationId, friendId, friendName, friendAvatar, initialMessage } = event.detail;
      
      if (!conversationId || !friendId) return;
      
      // Open the widget and set the active chat
      setIsOpen(true);
      setActiveChat({
        conversationId,
        friendId,
        friendName: friendName || 'User',
        friendAvatar: friendAvatar || null
      });
      
      // Load messages
      const msgResult = await fetchMessages(conversationId);
      if (msgResult.success) {
        setMessages(msgResult.data);
        if (user?.id) {
          markAsRead(conversationId, user.id);
          loadUnreadCount();
        }
        setTimeout(() => scrollToBottom(), 100);
      }
    };

    window.addEventListener('openDirectChat', handleOpenDirectChat);
    
    return () => {
      window.removeEventListener('openDirectChat', handleOpenDirectChat);
    };
  }, [user?.id]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      // Keep bubble within bounds on resize
      setBubblePosition(prev => ({
        x: Math.min(prev.x, window.innerWidth - friendsBubbleSize),
        y: Math.min(prev.y, window.innerHeight - friendsBubbleSize)
      }));
      setNerdAiBubblePosition(prev => ({
        x: Math.min(prev.x, window.innerWidth - nerdAiBubbleSize),
        y: Math.min(prev.y, window.innerHeight - nerdAiBubbleSize)
      }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [friendsBubbleSize, nerdAiBubbleSize]);

  // Drag handlers for mouse
  const handleMouseDown = (e) => {
    if (isOpen) return;
    setIsDragging(true);
    setHasMoved(false);
    setDragStart({
      x: e.clientX - bubblePosition.x,
      y: e.clientY - bubblePosition.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setHasMoved(true);
    const newX = Math.max(0, Math.min(window.innerWidth - friendsBubbleSize, e.clientX - dragStart.x));
    const newY = Math.max(0, Math.min(window.innerHeight - friendsBubbleSize, e.clientY - dragStart.y));
    setBubblePosition({ x: newX, y: newY });
  }, [isDragging, dragStart, friendsBubbleSize]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
    }
  }, [isDragging]);

  // Drag handlers for touch
  const handleTouchStart = (e) => {
    if (isOpen) return;
    // Don't preventDefault here - it blocks the click event
    const touch = e.touches[0];
    setIsDragging(true);
    setHasMoved(false);
    setDragStart({
      x: touch.clientX - bubblePosition.x,
      y: touch.clientY - bubblePosition.y
    });
  };

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault(); // Only prevent scroll while actually dragging
    e.stopPropagation();
    setHasMoved(true);
    const touch = e.touches[0];
    const newX = Math.max(0, Math.min(window.innerWidth - friendsBubbleSize, touch.clientX - dragStart.x));
    const newY = Math.max(0, Math.min(window.innerHeight - friendsBubbleSize, touch.clientY - dragStart.y));
    setBubblePosition({ x: newX, y: newY });
  }, [isDragging, dragStart, friendsBubbleSize]);

  const handleTouchEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      // If user didn't move much, treat it as a tap - check auth before opening
      if (!hasMoved) {
        if (!user) {
          openAuth('register');
          return;
        }
        if (!user?.profile?.completed_questionnaire) {
          toast.error('Please complete your profile first');
          return;
        }
        setBubblePosition({
          x: window.innerWidth - 80,
          y: window.innerHeight - 80
        });
        setTimeout(() => setIsOpen(true), 100);
      }
    }
  }, [isDragging, hasMoved, user, openAuth]);

  // ===== NERD AI BUBBLE DRAG HANDLERS =====
  const handleNerdAiMouseDown = (e) => {
    setIsNerdAiDragging(true);
    setNerdAiHasMoved(false);
    setNerdAiDragStart({
      x: e.clientX - nerdAiBubblePosition.x,
      y: e.clientY - nerdAiBubblePosition.y
    });
  };

  const handleNerdAiMouseMove = useCallback((e) => {
    if (!isNerdAiDragging) return;
    setNerdAiHasMoved(true);
    const newX = Math.max(0, Math.min(window.innerWidth - nerdAiBubbleSize, e.clientX - nerdAiDragStart.x));
    const newY = Math.max(0, Math.min(window.innerHeight - nerdAiBubbleSize, e.clientY - nerdAiDragStart.y));
    setNerdAiBubblePosition({ x: newX, y: newY });
  }, [isNerdAiDragging, nerdAiDragStart, nerdAiBubbleSize]);

  const handleNerdAiMouseUp = useCallback(() => {
    if (isNerdAiDragging) {
      setIsNerdAiDragging(false);
      // If user didn't move much, check auth then navigate to Ask Nerd AI
      if (!nerdAiHasMoved) {
        if (!user) {
          openAuth('register');
        } else if (!user?.profile?.completed_questionnaire) {
          toast.error('Please complete your profile first');
        } else {
          navigate('/ai-advisor');
        }
      }
    }
  }, [isNerdAiDragging, nerdAiHasMoved, navigate, user, openAuth]);

  const handleNerdAiTouchStart = (e) => {
    const touch = e.touches[0];
    setIsNerdAiDragging(true);
    setNerdAiHasMoved(false);
    setNerdAiDragStart({
      x: touch.clientX - nerdAiBubblePosition.x,
      y: touch.clientY - nerdAiBubblePosition.y
    });
  };

  const handleNerdAiTouchMove = useCallback((e) => {
    if (!isNerdAiDragging) return;
    e.preventDefault();
    e.stopPropagation();
    setNerdAiHasMoved(true);
    const touch = e.touches[0];
    const newX = Math.max(0, Math.min(window.innerWidth - nerdAiBubbleSize, touch.clientX - nerdAiDragStart.x));
    const newY = Math.max(0, Math.min(window.innerHeight - nerdAiBubbleSize, touch.clientY - nerdAiDragStart.y));
    setNerdAiBubblePosition({ x: newX, y: newY });
  }, [isNerdAiDragging, nerdAiDragStart, nerdAiBubbleSize]);

  const handleNerdAiTouchEnd = useCallback(() => {
    if (isNerdAiDragging) {
      setIsNerdAiDragging(false);
      // If user didn't move much, check auth then navigate to Ask Nerd AI
      if (!nerdAiHasMoved) {
        if (!user) {
          openAuth('register');
        } else if (!user?.profile?.completed_questionnaire) {
          toast.error('Please complete your profile first');
        } else {
          navigate('/ai-advisor');
        }
      }
    }
  }, [isNerdAiDragging, nerdAiHasMoved, navigate, user, openAuth]);

  // Add global mouse/touch event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd, { passive: false });
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Add global mouse/touch event listeners for Nerd AI bubble
  useEffect(() => {
    if (isNerdAiDragging) {
      window.addEventListener('mousemove', handleNerdAiMouseMove);
      window.addEventListener('mouseup', handleNerdAiMouseUp);
      window.addEventListener('touchmove', handleNerdAiTouchMove, { passive: false });
      window.addEventListener('touchend', handleNerdAiTouchEnd, { passive: false });
    }
    return () => {
      window.removeEventListener('mousemove', handleNerdAiMouseMove);
      window.removeEventListener('mouseup', handleNerdAiMouseUp);
      window.removeEventListener('touchmove', handleNerdAiTouchMove);
      window.removeEventListener('touchend', handleNerdAiTouchEnd);
    };
  }, [isNerdAiDragging, handleNerdAiMouseMove, handleNerdAiMouseUp, handleNerdAiTouchMove, handleNerdAiTouchEnd]);

  // Handle bubble click - animate back to corner and open (with auth check)
  const handleBubbleClick = () => {
    if (hasMoved) {
      // If was dragged, don't open, just reset hasMoved
      setHasMoved(false);
      return;
    }
    // Check auth before opening
    if (!user) {
      openAuth('register');
      return;
    }
    if (!user?.profile?.completed_questionnaire) {
      toast.error('Please complete your profile first');
      return;
    }
    // Animate back to default position and open
    setBubblePosition(defaultPosition);
    setTimeout(() => setIsOpen(true), 150);
  };

  // Initialize on mount
  useEffect(() => {
    if (user?.id && user?.full_name) {
      initializeData();
    }
  }, [user]);

  // Sync Supabase state
  useEffect(() => {
    setFriendRequests(pendingRequests);
  }, [pendingRequests]);

  useEffect(() => {
    setFriends(supabaseFriends);
  }, [supabaseFriends]);

  // Fetch unread message count (total and per friend)
  const loadUnreadCount = async () => {
    if (!user?.id) return;
    
    // Get total unread count
    const result = await getUnreadMessageCount(user.id);
    if (result.success) {
      setUnreadCount(result.count);
    }
    
    // Get per-friend unread counts
    const perFriendResult = await getUnreadCountsPerFriend(user.id);
    if (perFriendResult.success) {
      setUnreadPerFriend(perFriendResult.counts);
    }
  };

  // Load unread count on mount and when widget opens/closes
  useEffect(() => {
    if (user?.id) {
      loadUnreadCount();
    }
  }, [user?.id, isOpen]);

  // Subscribe to new messages for notification badge
  useEffect(() => {
    if (!user?.id) return;

    const channel = subscribeToAllUserMessages(user.id, (newMsg) => {
      // Only increment if widget is closed or not in the conversation
      if (!isOpen || activeChat?.conversationId !== newMsg.conversation_id) {
        setUnreadCount(prev => prev + 1);
        toast.info('New message received!');
      }
    });

    return () => {
      if (channel) unsubscribeChannel(channel);
    };
  }, [user?.id, isOpen, activeChat?.conversationId]);

  // Subscribe to friend requests
  // Subscribe to incoming friend requests
  useEffect(() => {
    if (!user?.id) return;

    const channel = subscribeToUserFriendRequests(user.id, () => {
      toast.info('New friend request!');
      loadFriendRequests();
    });

    return () => {
      if (channel) unsubscribe(channel);
    };
  }, [user?.id]);

  // Subscribe to friends list changes (when someone accepts your request)
  useEffect(() => {
    if (!user?.id) return;

    const channel = subscribeToFriendsList(user.id, () => {
      toast.success('Your friend request was accepted!');
      loadFriends();
      loadFriendRequests();
    });

    return () => {
      if (channel) unsubscribeChannel(channel);
    };
  }, [user?.id]);

  // Subscribe to chat messages when in chat
  useEffect(() => {
    if (!activeChat?.conversationId) return;

    const channel = subscribeToConversation(
      activeChat.conversationId,
      // On new message INSERT
      (newMsg) => {
        if (newMsg.sender_id !== user?.id) {
          setMessages(prev => {
            const exists = prev.some(m => m.id === newMsg.id);
            if (exists) return prev;
            return [...prev, newMsg];
          });
          markAsRead(activeChat.conversationId, user?.id);
        }
      },
      // On message UPDATE (for read receipts)
      (updatedMsg) => {
        setMessages(prev => prev.map(m => 
          m.id === updatedMsg.id ? { ...m, read_at: updatedMsg.read_at } : m
        ));
      }
    );

    return () => {
      if (channel) unsubscribe(channel);
    };
  }, [activeChat?.conversationId, user?.id]);

  // Subscribe to incoming calls
  useEffect(() => {
    if (!user?.id) return;

    const channel = subscribeToIncomingCalls(user.id, (payload) => {
      if (payload.eventType === 'INSERT' && payload.new.status === 'pending') {
        // New incoming call
        setIncomingCall(payload.new);
        setShowIncomingCall(true);
      } else if (payload.eventType === 'UPDATE') {
        // Call status changed
        if (payload.new.status === 'cancelled' || payload.new.status === 'ended') {
          setShowIncomingCall(false);
          setIncomingCall(null);
          if (payload.new.status === 'cancelled') {
            toast.info('Call was cancelled');
          }
        }
      }
    });

    return () => {
      if (channel) channel.unsubscribe();
    };
  }, [user?.id]);

  // Reload direct contacts when friends list changes
  useEffect(() => {
    if (user?.id && friends.length >= 0) {
      loadDirectContacts();
    }
  }, [friends, user?.id]);

  const initializeData = async () => {
    await ensureUserExists(user.id, user.full_name, user.avatar_url || null);
    await Promise.all([
      loadFriendRequests(),
      loadFriends(),
      loadDiscoveredUsers()
    ]);
    // Load direct contacts after friends are loaded (needs friend IDs for filtering)
    await loadDirectContacts();
  };

  // Load ALL friend requests (for debugging lost requests)
  const loadAllRequests = async () => {
    if (!user?.id) return;
    const result = await getAllFriendRequests(user.id);
    if (result.success) {
      setAllRequests(result.data);
    }
  };

  // Reset a stuck request back to pending
  const handleResetRequest = async (requestId) => {
    const result = await resetFriendRequest(requestId);
    if (result.success) {
      toast.success('Request reset to pending');
      loadFriendRequests();
      loadAllRequests();
    } else {
      toast.error('Failed to reset request');
    }
  };

  const loadFriendRequests = async () => {
    if (!user?.id) return;
    await fetchFriendRequests(user.id);
  };

  const loadFriends = async () => {
    if (!user?.id) return;
    await fetchFriends(user.id);
  };

  // Load direct contacts (conversations from non-friends, e.g., employers)
  const loadDirectContacts = async () => {
    if (!user?.id) return;
    
    try {
      const result = await getAllUserConversations(user.id);
      if (result.success) {
        // Get friend IDs to filter them out
        const friendIds = new Set(friends.map(f => f.friend?.id));
        
        // Filter to only show conversations with non-friends
        const nonFriendConversations = result.data.filter(
          conv => !friendIds.has(conv.other_user?.id)
        );
        
        // Check each contact for admin status and contact type
        const enrichedContacts = await Promise.all(
          nonFriendConversations.map(async (conv) => {
            try {
              // Check if other user is admin/subadmin
              const adminResponse = await axios.get(`${API}/users/${conv.other_user?.id}/is-admin`);
              const isOtherAdmin = adminResponse.data.is_admin;
              
              // Get contact type metadata
              const typeResult = await getConversationContactType(conv.id);
              const contactType = typeResult.contactType || 'unknown';
              const initiatorId = typeResult.initiatorId;
              
              // Determine display category based on perspective
              let displayCategory;
              if (isAdminOrSubadmin) {
                // Admin's view: categorize by contact type they initiated
                if (contactType === 'support') {
                  displayCategory = 'support';
                } else if (contactType === 'job') {
                  displayCategory = 'job';
                } else if (initiatorId === user.id) {
                  // Unknown type but we initiated - assume support
                  displayCategory = 'support';
                } else if (initiatorId && initiatorId !== user.id) {
                  // Someone contacted us
                  displayCategory = 'incoming';
                } else {
                  // No metadata - for legacy contacts, put in support if we're viewing
                  displayCategory = 'support';
                }
              } else {
                // Regular user's view
                if (isOtherAdmin) {
                  displayCategory = 'support_team'; // Message from admin
                } else {
                  displayCategory = 'job'; // Message from employer
                }
              }
              
              return {
                ...conv,
                is_support_team: isOtherAdmin,
                contact_type: contactType,
                display_category: displayCategory
              };
            } catch {
              return { 
                ...conv, 
                is_support_team: false, 
                contact_type: 'unknown',
                display_category: isAdminOrSubadmin ? 'support' : 'job'
              };
            }
          })
        );
        
        setDirectContacts(enrichedContacts);
      }
    } catch (error) {
      console.error('Error loading direct contacts:', error);
    }
  };

  const loadDiscoveredUsers = async (search = '') => {
    try {
      // Step 1: Fetch users from MongoDB with optional search
      const url = search 
        ? `${API}/friends/discover?search=${encodeURIComponent(search)}`
        : `${API}/friends/discover`;
      
      const response = await axios.get(url);
      if (!response.data.users) {
        setDiscoveredUsers([]);
        return;
      }

      const mongoUsers = response.data.users;

      // Step 2: Get excluded IDs from Supabase (friends + friend_requests)
      const { excludedIds } = await getExcludedUserIds(user?.id);

      // Step 3: Filter out excluded users
      const filteredUsers = mongoUsers.filter(u => !excludedIds.includes(u.id));

      setDiscoveredUsers(filteredUsers);
    } catch (error) {
      console.error('Error discovering users:', error);
      setDiscoveredUsers([]);
    }
  };

  const handleSendFriendRequest = async (receiverId, receiverName) => {
    if (!requireAuth()) return;

    setSendingRequest(prev => ({ ...prev, [receiverId]: true }));
    
    // Ensure BOTH sender and receiver exist in Supabase before sending request
    await ensureUserExists(user.id, user.full_name, user.avatar_url || null);
    await ensureUserExists(receiverId, receiverName, null);
    
    const result = await sendFriendRequest(user, receiverId);
    setSendingRequest(prev => ({ ...prev, [receiverId]: false }));

    if (result.success) {
      setDiscoveredUsers(prev => prev.filter(u => u.id !== receiverId));
    }
  };

  const handleAcceptRequest = async (requestId, requesterId, requesterName) => {
    if (!requireAuth()) return;
    const result = await acceptFriendRequest(user, requestId, requesterId, requesterName);
    if (result.success) {
      loadDiscoveredUsers();
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!requireAuth()) return;
    await rejectFriendRequest(user, requestId);
  };

  const openChat = async (friendData) => {
    if (!requireAuth()) return;
    
    if (!friendData?.friend?.id) {
      toast.error('Unable to open chat');
      return;
    }

    setLoading(true);
    setIsChatWithSupportTeam(false); // Regular friend chat
    const result = await openConversation(user, friendData.friend.id);

    if (result.success) {
      setActiveChat({
        conversationId: result.data.id,
        friendId: friendData.friend.id,
        friendName: friendData.friend.name,
        friendAvatar: friendData.friend.avatar_url
      });
      
      // Load messages
      const msgResult = await fetchMessages(result.data.id);
      if (msgResult.success) {
        setMessages(msgResult.data);
        markAsRead(result.data.id, user?.id);
        // Refresh unread count after marking as read
        loadUnreadCount();
        // Scroll to bottom after messages load
        setTimeout(() => scrollToBottom(), 100);
      }
    }
    setLoading(false);
  };

  // Open chat from direct contact (employer/non-friend conversations)
  const openDirectContactChat = async (contact) => {
    if (!requireAuth()) return;
    
    setLoading(true);
    
    // Check if this is a support team chat
    const isSupportChat = contact.is_support_team || false;
    setIsChatWithSupportTeam(isSupportChat);
    
    setActiveChat({
      conversationId: contact.id,
      friendId: contact.other_user?.id,
      friendName: isSupportChat ? 'Nerd AI Support Team' : (contact.other_user?.name || 'Unknown'),
      friendAvatar: contact.other_user?.avatar_url
    });
    
    // Load messages
    const msgResult = await fetchMessages(contact.id);
    if (msgResult.success) {
      setMessages(msgResult.data);
      markAsRead(contact.id, user?.id);
      loadUnreadCount();
      setTimeout(() => scrollToBottom(), 100);
    }
    setLoading(false);
  };

  // Admin/Subadmin direct message (bypasses friend request)
  const handleAdminDirectMessage = async (targetUser) => {
    if (!requireAuth()) return;
    if (!isAdminOrSubadmin) return;

    setLoading(true);
    try {
      const result = await createDirectContact(
        user.id,
        user.full_name,
        targetUser.id,
        targetUser.full_name,
        null, // No auto message for admin contact
        'support' // Contact type for support
      );

      if (result.success) {
        // Open the chat directly
        setActiveChat({
          conversationId: result.conversationId,
          friendId: targetUser.id,
          friendName: targetUser.full_name,
          friendAvatar: null
        });

        // Load messages
        const msgResult = await fetchMessages(result.conversationId);
        if (msgResult.success) {
          setMessages(msgResult.data);
          markAsRead(result.conversationId, user?.id);
          loadUnreadCount();
          setTimeout(() => scrollToBottom(), 100);
        }

        // Reload direct contacts
        loadDirectContacts();
      } else {
        toast.error('Failed to start conversation');
      }
    } catch (error) {
      console.error('Error starting direct message:', error);
      toast.error('Failed to start conversation');
    }
    setLoading(false);
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    const messageText = newMessage.trim();
    if (!messageText || sending || !activeChat?.conversationId) return;

    setSending(true);

    // Optimistic update
    const tempId = `temp_${Date.now()}`;
    const optimisticMessage = {
      id: tempId,
      conversation_id: activeChat.conversationId,
      sender_id: user.id,
      message: messageText,
      created_at: new Date().toISOString(),
      sender: { id: user.id, name: user.full_name }
    };

    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage('');
    // Scroll to bottom when sending
    setTimeout(() => scrollToBottom(), 50);

    const result = await sendMessageToSupabase(activeChat.conversationId, user, messageText);

    if (result.success) {
      setMessages(prev => prev.map(m => 
        m.id === tempId ? { ...result.data, sender: optimisticMessage.sender } : m
      ));
    } else {
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setNewMessage(messageText);
    }

    setSending(false);
  };

  // Handle emoji selection
  const onEmojiClick = (emojiData) => {
    setNewMessage(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  // Handle GIF selection
  const onGifClick = async (gif, e) => {
    e.preventDefault();
    setShowGifPicker(false);
    setGifSearchQuery('');
    
    if (!activeChat?.conversationId) return;
    
    setSending(true);
    
    // Send GIF as a message with the GIF URL
    const result = await sendMessageWithFile(
      activeChat.conversationId,
      user.id,
      '',
      {
        url: gif.images.fixed_height.url,
        name: gif.title || 'GIF',
        type: 'gif'
      }
    );
    
    if (result.success) {
      setMessages(prev => [...prev, {
        ...result.data,
        sender: { id: user.id, name: user.full_name }
      }]);
      setTimeout(() => scrollToBottom(), 50);
    } else {
      toast.error('Failed to send GIF');
    }
    
    setSending(false);
  };

  // Fetch GIFs function
  const fetchGifs = (offset) => {
    if (gifSearchQuery.trim()) {
      return gf.search(gifSearchQuery, { offset, limit: 10 });
    }
    return gf.trending({ offset, limit: 10 });
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
      if (gifPickerRef.current && !gifPickerRef.current.contains(event.target)) {
        setShowGifPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if user is blocked when opening chat
  useEffect(() => {
    const checkBlockStatus = async () => {
      if (activeChat?.friendId && user?.id) {
        const result = await isUserBlocked(user.id, activeChat.friendId);
        setIsBlocked(result.isBlocked);
      }
    };
    checkBlockStatus();
  }, [activeChat?.friendId, user?.id]);

  // Handle file upload
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeChat?.conversationId) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    
    const uploadResult = await uploadChatFile(file, activeChat.conversationId);
    
    if (uploadResult.success) {
      // Send message with file
      const msgResult = await sendMessageWithFile(
        activeChat.conversationId,
        user.id,
        '',
        uploadResult.data
      );

      if (msgResult.success) {
        setMessages(prev => [...prev, {
          ...msgResult.data,
          sender: { id: user.id, name: user.full_name }
        }]);
      }
    } else {
      toast.error('Failed to upload file');
    }

    setUploading(false);
    e.target.value = ''; // Reset input
  };

  // Daily.co Call Functions with Signaling
  const generateRoomId = () => {
    // Create unique room ID based on both user IDs (sorted for consistency)
    const ids = [user.id, activeChat?.friendId].sort();
    return `nerdai-${ids[0].slice(0, 8)}-${ids[1].slice(0, 8)}-${Date.now().toString(36)}`;
  };

  const startVoiceCall = async () => {
    if (!activeChat?.friendId) return;
    const roomId = generateRoomId();
    
    // Send call signal to the other user via Supabase
    const result = await initiateCall(
      user.id,
      user.full_name || 'User',
      user.avatar_url,
      activeChat.friendId,
      activeChat.friendName,
      roomId,
      false // voice call
    );
    
    if (result.success) {
      setCurrentCallId(result.call.id);
      setCallRoomId(roomId);
      setIsVideoCall(false);
      setShowCallModal(true);
      toast.info(`Calling ${activeChat.friendName}...`);
      
      // Subscribe to call status changes
      const channel = subscribeToCallStatus(result.call.id, (call) => {
        if (call.status === 'rejected') {
          toast.error(`${activeChat.friendName} declined the call`);
          handleCloseCall();
        } else if (call.status === 'accepted') {
          toast.success(`${activeChat.friendName} joined the call!`);
        }
      });
      callStatusChannelRef.current = channel;
    } else {
      toast.error('Failed to start call');
    }
  };

  const startVideoCall = async () => {
    if (!activeChat?.friendId) return;
    const roomId = generateRoomId();
    
    // Send call signal to the other user via Supabase
    const result = await initiateCall(
      user.id,
      user.full_name || 'User',
      user.avatar_url,
      activeChat.friendId,
      activeChat.friendName,
      roomId,
      true // video call
    );
    
    if (result.success) {
      setCurrentCallId(result.call.id);
      setCallRoomId(roomId);
      setIsVideoCall(true);
      setShowCallModal(true);
      toast.info(`Calling ${activeChat.friendName}...`);
      
      // Subscribe to call status changes
      const channel = subscribeToCallStatus(result.call.id, (call) => {
        if (call.status === 'rejected') {
          toast.error(`${activeChat.friendName} declined the call`);
          handleCloseCall();
        } else if (call.status === 'accepted') {
          toast.success(`${activeChat.friendName} joined the call!`);
        }
      });
      callStatusChannelRef.current = channel;
    } else {
      toast.error('Failed to start call');
    }
  };

  const handleCloseCall = async () => {
    // Cancel the call if still pending
    if (currentCallId) {
      await cancelCall(currentCallId);
      if (callStatusChannelRef.current) {
        callStatusChannelRef.current.unsubscribe();
      }
    }
    setShowCallModal(false);
    setCallRoomId('');
    setCurrentCallId(null);
    toast.info('Call ended');
  };

  // Handle incoming call actions
  const handleAcceptCall = async () => {
    if (!incomingCall) return;
    
    const result = await respondToCall(incomingCall.id, 'accept');
    if (result.success) {
      setCallRoomId(incomingCall.room_id);
      setIsVideoCall(incomingCall.is_video);
      setCurrentCallId(incomingCall.id);
      setShowIncomingCall(false);
      setShowCallModal(true);
      setIncomingCall(null);
      toast.success(`Joined call with ${incomingCall.caller_name}`);
    }
  };

  const handleRejectCall = async () => {
    if (!incomingCall) return;
    
    await respondToCall(incomingCall.id, 'reject');
    setShowIncomingCall(false);
    setIncomingCall(null);
    toast.info('Call declined');
  };

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        // Show our branded permissions modal so the user can grant access
        window.dispatchEvent(new CustomEvent('nn-request-permissions'));
      } else {
        toast.error('Could not access microphone. Please check your device settings.');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setAudioBlob(null);
    setRecordingTime(0);
    clearInterval(recordingTimerRef.current);
    audioChunksRef.current = [];
  };

  const sendVoiceMessage = async () => {
    if (!audioBlob || !activeChat?.conversationId) return;

    setUploading(true);

    try {
      // Create a File object from the blob
      const audioFile = new File([audioBlob], `voice_${Date.now()}.webm`, { type: 'audio/webm' });
      
      const uploadResult = await uploadChatFile(audioFile, activeChat.conversationId);
      
      if (uploadResult.success) {
        const msgResult = await sendMessageWithFile(
          activeChat.conversationId,
          user.id,
          '🎤 Voice message',
          { ...uploadResult.data, type: 'audio' }
        );

        if (msgResult.success) {
          setMessages(prev => [...prev, {
            ...msgResult.data,
            sender: { id: user.id, name: user.full_name }
          }]);
          toast.success('Voice message sent!');
        }
      } else {
        toast.error('Failed to send voice message');
      }
    } catch (error) {
      console.error('Error sending voice message:', error);
      toast.error('Failed to send voice message');
    }

    setAudioBlob(null);
    setRecordingTime(0);
    setUploading(false);
  };

  // Format recording time
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup recording on unmount or chat close
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // Handle block/unblock
  const handleBlockUser = async () => {
    if (!user?.id || !activeChat?.friendId) return;

    // Check if trying to block admin/subadmin (not allowed)
    try {
      const response = await axios.get(`${API}/users/${activeChat.friendId}/is-admin`);
      if (response.data.is_admin) {
        toast.error('Cannot block portal support team');
        setShowBlockMenu(false);
        return;
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }

    if (isBlocked) {
      const result = await unblockUser(user.id, activeChat.friendId);
      if (result.success) {
        setIsBlocked(false);
        toast.success('User unblocked');
      }
    } else {
      const result = await blockUser(user.id, activeChat.friendId);
      if (result.success) {
        setIsBlocked(true);
        toast.success('User blocked');
      }
    }
    setShowBlockMenu(false);
  };

  // Handle delete single message
  const handleDeleteMessage = async (msgId) => {
    if (!user?.id) return;
    setDeletingMsgId(msgId);
    const result = await deleteMessage(msgId, user.id);
    if (result.success) {
      setMessages(prev => prev.filter(m => m.id !== msgId));
    } else {
      toast.error('Could not delete message');
    }
    setDeletingMsgId(null);
  };

  // Handle delete all messages in the conversation
  const handleDeleteAllMessages = async () => {
    if (!activeChat?.conversationId) return;
    setIsDeletingAll(true);
    setShowBlockMenu(false);
    const result = await deleteAllMessages(activeChat.conversationId);
    if (result.success) {
      setMessages([]);
      toast.success('All messages deleted');
    } else {
      toast.error('Could not delete conversation');
    }
    setIsDeletingAll(false);
  };
  const renderFileContent = (msg) => {
    if (!msg.file_url) return null;

    // GIF support
    if (msg.file_type === 'gif') {
      return (
        <img 
          src={msg.file_url} 
          alt={msg.file_name || 'GIF'} 
          className="max-w-full rounded-lg"
          style={{ maxHeight: '200px' }} loading="lazy" decoding="async"/>
      );
    }

    if (msg.file_type === 'image') {
      return (
        <img 
          src={msg.file_url} 
          alt={msg.file_name || 'Image'} 
          className="max-w-full rounded-lg cursor-pointer hover:opacity-90"
          onClick={() => window.open(msg.file_url, '_blank')} loading="lazy" decoding="async" />
      );
    }

    if (msg.file_type === 'video') {
      return (
        <video controls className="max-w-full rounded-lg">
          <source src={msg.file_url} />
        </video>
      );
    }

    if (msg.file_type === 'audio') {
      return <VoiceMessagePlayer src={msg.file_url} isOwn={msg.sender_id === user?.id} />;
    }

    // Document/other files
    return (
      <a 
        href={msg.file_url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-2 p-2 bg-white/20 rounded-lg hover:bg-white/30"
      >
        <FileText className="h-5 w-5" />
        <span className="text-sm truncate">{msg.file_name || 'Download file'}</span>
      </a>
    );
  };

  // Render link in message
  const renderMessageContent = (text) => {
    if (!text) return null;
    
    // URL regex
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, i) => {
      if (part.match(urlRegex)) {
        return (
          <a 
            key={i} 
            href={part} 
            target="_blank" 
            rel="noopener noreferrer"
            className="underline hover:opacity-80"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const formatTime = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return '';
    }
  };

  // Debounced search - calls API when user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (activeTab === 'discover') {
        loadDiscoveredUsers(searchQuery);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, activeTab]);

  // No local filtering needed - API does the search
  const filteredDiscovered = discoveredUsers;

  const totalNotifications = friendRequests.length + unreadCount;

  // Helper function to check if user can perform actions
  const requireAuth = (action) => {
    if (!user) {
      openAuth('register');
      return false;
    }
    if (!user?.profile?.completed_questionnaire) {
      toast.error('Please complete your profile first');
      return false;
    }
    return true;
  };

  // Chat View - Mobile optimized
  if (activeChat) {
    return (
      <div id="chat-widget-panel" className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-96 sm:max-w-[calc(100vw-3rem)] h-[85vh] sm:h-[500px] sm:max-h-[70vh] bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl border border-amber-400 flex flex-col overflow-hidden">
        {/* Chat Header - Amber/Gold theme */}
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-300 p-3 sm:p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setActiveChat(null);
                setMessages([]);
                setIsBlocked(false);
                setIsChatWithSupportTeam(false);
                cancelRecording();
              }}
              className="p-1 hover:bg-amber-100 text-amber-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Avatar className="w-8 h-8 ring-2 ring-amber-400">
              {isChatWithSupportTeam ? (
                <img 
                  src="https://customer-assets.emergentagent.com/job_736cdda7-e974-4fad-aa62-3a758e69e883/artifacts/hc6xcw6c_Time%282%29.png" 
                  alt="Support Team" 
                  className="w-full h-full object-cover rounded-full" loading="lazy" decoding="async"/>
              ) : activeChat.friendAvatar ? (
                <img src={activeChat.friendAvatar} alt="" className="w-full h-full object-cover rounded-full" loading="lazy" decoding="async"/>
              ) : (
                <AvatarFallback className="bg-amber-100 text-amber-700 text-sm font-semibold">
                  {activeChat.friendName?.charAt(0).toUpperCase() || 'F'}
                </AvatarFallback>
              )}
            </Avatar>
            <span className="font-semibold truncate text-amber-900">{activeChat.friendName}</span>
          </div>
          
          {/* Block Menu */}
          <div className="flex items-center space-x-1">
            {/* Voice & Video Call Buttons - Hidden for now
            <button
              onClick={startVoiceCall}
              className="p-2 hover:bg-green-100 rounded-full text-green-600 transition-colors"
              title="Voice Call"
            >
              <Phone className="h-5 w-5" />
            </button>
            <button
              onClick={startVideoCall}
              className="p-2 hover:bg-blue-100 rounded-full text-blue-600 transition-colors"
              title="Video Call"
            >
              <Video className="h-5 w-5" />
            </button>
            */}
            
            {/* More Options Menu - Hidden for Support Team */}
            {!isChatWithSupportTeam && (
              <div className="relative">
                <button 
                  onClick={() => setShowBlockMenu(!showBlockMenu)}
                  className="p-2 hover:bg-amber-100 rounded-full text-amber-700 transition-colors"
                  title="More options"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
                {showBlockMenu && (
                  <div className="absolute right-0 top-10 z-50 min-w-[190px] rounded-xl overflow-hidden shadow-xl border border-amber-300">
                    {/* Header strip matching chat header */}
                    <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-4 py-1.5">
                      <span style={{ fontSize: '8px' }} className="text-white font-semibold uppercase tracking-wide">Options</span>
                    </div>
                    <div className="bg-gradient-to-b from-amber-50 to-yellow-50">
                      <button
                        onClick={handleBlockUser}
                        className={`w-full px-4 py-1.5 text-left flex items-center border-b border-amber-100 ${isBlocked ? 'hover:bg-green-50 text-green-700' : 'hover:bg-red-50 text-red-600'}`}
                      >
                        <span style={{ fontSize: '10px', fontWeight: 500 }}>{isBlocked ? 'Unblock User' : 'Block User'}</span>
                      </button>
                      <button
                        onClick={handleDeleteAllMessages}
                        disabled={isDeletingAll}
                        className="w-full px-4 py-1.5 text-left flex items-center hover:bg-red-50 text-red-600"
                      >
                        {isDeletingAll
                          ? <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin mr-2" />
                          : null
                        }
                        <span style={{ fontSize: '10px', fontWeight: 500 }}>Delete All Conversations</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-3 bg-gray-50" style={{ overscrollBehavior: 'contain' }}>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              <MessageCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              Start the conversation!
            </div>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.sender_id === user?.id;
              return (
                <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}>
                  <div className="flex items-end gap-1.5 max-w-[80%]">
                    {/* Delete button — left of own messages, right of received */}
                    {isOwn && (
                      <button
                        onClick={() => handleDeleteMessage(msg.id)}
                        disabled={deletingMsgId === msg.id}
                        className="opacity-0 group-hover:opacity-100 sm:opacity-0 sm:group-hover:opacity-100 opacity-100 flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-all mb-1"
                        title="Delete message"
                      >
                        {deletingMsgId === msg.id
                          ? <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin" />
                          : <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        }
                      </button>
                    )}
                    <div className={`rounded-2xl px-3 py-2 ${
                      isOwn
                        ? 'bg-gradient-to-r from-blue-700 to-blue-800 text-white'
                        : 'bg-white border border-gray-200'
                    }`}>
                      {/* File Content */}
                      {msg.file_url && (
                        <div className="mb-2">
                          {renderFileContent(msg)}
                        </div>
                      )}
                      
                      {/* Text Message with Links */}
                      {msg.message && (
                        <p className="text-sm whitespace-pre-wrap break-words">
                          {renderMessageContent(msg.message)}
                        </p>
                      )}
                      
                      <div className={`flex items-center justify-end gap-1 mt-1 ${isOwn ? 'text-orange-100' : 'text-gray-400'}`}>
                        <span className="text-xs">{formatTime(msg.created_at)}</span>
                        {isOwn && (
                          <span className={`text-xs ${msg.read_at ? 'text-blue-400' : ''}`}>
                            {msg.read_at ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>

        {/* Blocked User Notice */}
        {isBlocked && (
          <div className="p-3 bg-red-50 border-t border-red-200 text-center text-sm text-red-600">
            You have blocked this user. Unblock to send messages.
          </div>
        )}

        {/* Message Input */}
        {!isBlocked && (
          <div className="p-3 border-t bg-white relative" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0.75rem))' }}>
            {/* Emoji Picker - Full width bottom sheet on both mobile and desktop */}
            {showEmojiPicker && (
              <div 
                ref={emojiPickerRef} 
                className="absolute left-0 right-0 bottom-0 z-50 bg-white rounded-t-xl shadow-2xl border-t border-amber-300 overflow-hidden"
              >
                <div className="flex items-center justify-between p-2 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-yellow-50">
                  <span className="text-sm font-semibold text-amber-900">😊 Emoji</span>
                  <button 
                    onClick={() => setShowEmojiPicker(false)}
                    className="p-1 hover:bg-amber-100 rounded-full"
                  >
                    <X className="h-4 w-4 text-amber-700" />
                  </button>
                </div>
                <EmojiPicker 
                  onEmojiClick={onEmojiClick} 
                  width="100%"
                  height={280}
                  searchPlaceHolder="Search emoji..."
                  searchDisabled={true}
                  skinTonesDisabled={true}
                  lazyLoadEmojis={true}
                  previewConfig={{ showPreview: false }}
                  suggestedEmojisMode="frequent"
                  emojiStyle="apple"
                  categories={[
                    { name: 'Recently Used', category: 'suggested' },
                    { name: 'Smileys', category: 'smileys_people' },
                    { name: 'Animals', category: 'animals_nature' },
                    { name: 'Food', category: 'food_drink' },
                    { name: 'Activities', category: 'activities' },
                    { name: 'Travel', category: 'travel_places' },
                    { name: 'Objects', category: 'objects' },
                    { name: 'Symbols', category: 'symbols' },
                    { name: 'Flags', category: 'flags' }
                  ]}
                />
              </div>
            )}
            
            {/* GIF Picker - Full width bottom sheet on both mobile and desktop */}
            {showGifPicker && (
              <div 
                ref={gifPickerRef} 
                className="absolute left-0 right-0 bottom-0 z-50 bg-white rounded-t-xl shadow-2xl border-t border-amber-300 overflow-hidden"
                style={{ height: '320px' }}
              >
                <div className="p-2 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-yellow-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-amber-900">🎬 GIFs</span>
                    <button 
                      onClick={() => setShowGifPicker(false)}
                      className="p-1 hover:bg-amber-100 rounded-full"
                    >
                      <X className="h-4 w-4 text-amber-700" />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={gifSearchQuery}
                    onChange={(e) => setGifSearchQuery(e.target.value)}
                    placeholder="Search GIFs..."
                    className="w-full px-3 py-1.5 text-sm border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                </div>
                <div className="overflow-y-auto" style={{ height: 'calc(100% - 80px)' }}>
                  <Grid
                    key={gifSearchQuery}
                    width={380}
                    columns={3}
                    fetchGifs={fetchGifs}
                    onGifClick={onGifClick}
                    hideAttribution={true}
                    noLink={true}
                  />
                </div>
                <div className="text-center text-[10px] text-gray-400 py-1 bg-gray-50 border-t">
                  Powered by GIPHY
                </div>
              </div>
            )}
            
            {/* Hidden File Input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            />
            
            {/* Voice Recording UI */}
            {isRecording ? (
              <div className="flex items-center justify-between bg-red-50 rounded-full px-4 py-2 border border-red-200">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-red-600 font-medium">{formatRecordingTime(recordingTime)}</span>
                  <span className="text-red-500 text-sm">Recording...</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={cancelRecording}
                    className="p-2 transition-colors text-red-400 hover:text-red-600"
                    title="Cancel"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={stopRecording}
                    className="p-2 transition-colors text-red-500 hover:text-red-700"
                    title="Stop Recording"
                  >
                    <Square className="h-4 w-4" fill="currentColor" />
                  </button>
                </div>
              </div>
            ) : audioBlob ? (
              /* Audio Preview UI */
              <div className="flex items-center justify-between bg-amber-50 rounded-full px-4 py-2 border border-amber-200">
                <div className="flex items-center space-x-3">
                  <Mic className="h-5 w-5 text-amber-600" />
                  <span className="text-amber-700 font-medium">{formatRecordingTime(recordingTime)}</span>
                  <audio src={URL.createObjectURL(audioBlob)} controls className="h-8 max-w-[120px]" />
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={cancelRecording}
                    className="p-2 transition-colors text-amber-500 hover:text-amber-700"
                    title="Discard"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={sendVoiceMessage}
                    disabled={uploading}
                    className="p-2 transition-colors text-amber-500 hover:text-amber-700"
                    title="Send Voice Message"
                  >
                    {uploading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-500" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* Normal Message Input */
              <form onSubmit={handleSendMessage} className="flex items-center space-x-1 sm:space-x-2">
                {/* Attachment Button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                >
                  {uploading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-500" />
                  ) : (
                    <Paperclip className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                {/* Voice Record Button */}
                <button
                  type="button"
                  onClick={startRecording}
                  disabled={uploading || sending}
                  className="p-1.5 sm:p-2 hover:bg-amber-50 rounded-full transition-colors group flex-shrink-0"
                  title="Record Voice Message"
                >
                  <Mic className="h-5 w-5 text-gray-500 group-hover:text-amber-600" />
                </button>
                
                {/* GIF Button */}
                <button
                  type="button"
                  onClick={() => {
                    setShowGifPicker(!showGifPicker);
                    setShowEmojiPicker(false);
                  }}
                  disabled={uploading || sending}
                  className="p-1.5 sm:p-2 hover:bg-purple-50 rounded-full transition-colors group flex-shrink-0"
                  title="Send GIF"
                >
                  <span className="text-lg text-gray-500 group-hover:scale-110 transition-transform">GIF</span>
                </button>
                
                {/* Text Input */}
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 text-sm min-w-0 text-gray-900"
                  disabled={sending || uploading}
                />
                
                {/* Emoji Button - Now on right side of text input */}
                <button
                  type="button"
                  onClick={() => {
                    setShowEmojiPicker(!showEmojiPicker);
                    setShowGifPicker(false);
                  }}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                >
                  <Smile className="h-5 w-5 text-gray-500" />
                </button>
                
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sending || uploading}
                  size="sm"
                  className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 flex-shrink-0"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            )}
          </div>
        )}
      </div>
    );
  }

  // Main Widget
  if (permsShowing) return null;

  return (
    <>
      {/* Video/Voice Call Modals - Hidden for now
      <VideoCallModal
        isOpen={showCallModal}
        onClose={handleCloseCall}
        roomId={callRoomId}
        userName={user?.full_name || 'User'}
        userAvatar={user?.avatar_url}
        friendName={activeChat?.friendName || 'Friend'}
        isVideoCall={isVideoCall}
      />
      <IncomingCallModal
        isOpen={showIncomingCall}
        callerName={incomingCall?.caller_name || 'Someone'}
        callerAvatar={incomingCall?.caller_avatar}
        isVideo={incomingCall?.is_video}
        onAccept={handleAcceptCall}
        onReject={handleRejectCall}
      />
      */}
      
      {/* Ask Nerd AI Floating Button - Hidden on AI Advisor page and on mobile/tablet (< 768px) */}
      {!isOpen && nerdAiBubblePosition.x !== null && location.pathname !== '/ai-advisor' && (
        <button
          ref={nerdAiBubbleRef}
          onMouseDown={handleNerdAiMouseDown}
          onTouchStart={handleNerdAiTouchStart}
          onClick={() => {
            if (!nerdAiHasMoved) {
              if (!user) {
                openAuth('register');
              } else if (!user?.profile?.completed_questionnaire) {
                toast.error('Please complete your profile first');
              } else {
                navigate('/ai-advisor');
              }
            }
            setNerdAiHasMoved(false);
          }}
          className="hidden md:block"
          style={{
            position: 'fixed',
            left: nerdAiBubblePosition.x,
            top: nerdAiBubblePosition.y,
            width: `${nerdAiBubbleSize}px`,
            height: `${nerdAiBubbleSize}px`,
            borderRadius: '50%',
            overflow: 'hidden',
            touchAction: 'none',
            border: 'none',
            padding: 0,
            outline: 'none',
            zIndex: 50,
            cursor: isNerdAiDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            /* idle bob — paused while dragging */
            animation: isNerdAiDragging
              ? 'none'
              : 'nerdai-bob 3s ease-in-out infinite',
            transition: isNerdAiDragging ? 'none' : 'left 0.3s cubic-bezier(0.34,1.56,0.64,1), top 0.3s cubic-bezier(0.34,1.56,0.64,1)',
            /* green metallic rim — layered box-shadows for physical thickness */
            boxShadow: `
              0 0 0 3px #1a7a3a,
              0 0 0 5px #2ecc71,
              0 0 0 7px #145c2b,
              0 0 14px 4px rgba(46,204,113,0.55),
              0 6px 20px rgba(0,0,0,0.55)
            `,
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_736cdda7-e974-4fad-aa62-3a758e69e883/artifacts/9om5szqv_Time%287%29.png)',
            backgroundSize: '200%',
            backgroundPosition: 'center',
            backgroundColor: '#0f5c25',
          }}
          title="Ask Nerd AI"
        >
          {/* Edge mask — green radial overlay to seal any dark image border */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%', pointerEvents: 'none',
            background: 'radial-gradient(circle, transparent 52%, #0f5c25 64%, #0f5c25 100%)',
          }} />

          {/* Specular highlight — static soft ellipse top-left */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%', pointerEvents: 'none',
            background: 'radial-gradient(ellipse 55% 40% at 28% 22%, rgba(180,255,200,0.32) 0%, transparent 70%)',
          }} />

          {/* Shimmer sweep 1 */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%', pointerEvents: 'none',
            overflow: 'hidden', clipPath: 'circle(50% at 50% 50%)',
          }}>
            <div style={{
              position: 'absolute', top: 0, bottom: 0, width: '45%',
              background: 'linear-gradient(90deg, transparent 0%, rgba(100,255,150,0.28) 50%, transparent 100%)',
              mixBlendMode: 'screen',
              animation: 'nerdai-shimmer 2.4s ease-in-out infinite',
            }} />
          </div>

          {/* Shimmer sweep 2 — green-tinted, delayed 0.3s */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%', pointerEvents: 'none',
            overflow: 'hidden', clipPath: 'circle(50% at 50% 50%)',
          }}>
            <div style={{
              position: 'absolute', top: 0, bottom: 0, width: '30%',
              background: 'linear-gradient(90deg, transparent 0%, rgba(60,220,120,0.18) 50%, transparent 100%)',
              mixBlendMode: 'screen',
              animation: 'nerdai-shimmer 2.4s ease-in-out 0.3s infinite',
            }} />
          </div>
        </button>
      )}
      
      {/* Floating Button - Draggable (Friends Chat) - Hidden on mobile/tablet (< 768px) */}
      {!isOpen && bubblePosition.x !== null && (
        <div className="hidden md:block" style={{
          position: 'fixed',
          left: bubblePosition.x,
          top: bubblePosition.y,
          width: `${friendsBubbleSize}px`,
          height: `${friendsBubbleSize}px`,
          zIndex: 50,
          transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}>
          <button
            ref={bubbleRef}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onClick={handleBubbleClick}
            style={{
              position: 'absolute',
              inset: 0,
              width: `${friendsBubbleSize}px`,
              height: `${friendsBubbleSize}px`,
              borderRadius: '50%',
              overflow: 'hidden',
              touchAction: 'none',
              border: 'none',
              padding: 0,
              outline: 'none',
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_nerdstudio/artifacts/n76lrf21_Creator%20AI%286%29.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'saturate(1.3) brightness(1.15)',
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
              transform: isDragging ? 'scale(1.1)' : undefined,
            }}
          >
            {/* Shimmer sweep 1 — white */}
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%', pointerEvents: 'none',
              overflow: 'hidden', clipPath: 'circle(50% at 50% 50%)',
            }}>
              <div style={{
                position: 'absolute', top: 0, bottom: 0, width: '45%',
                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)',
                mixBlendMode: 'screen',
                animation: 'nerdai-shimmer 2.6s ease-in-out infinite',
              }} />
            </div>

            {/* Shimmer sweep 2 — delayed 0.3s */}
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%', pointerEvents: 'none',
              overflow: 'hidden', clipPath: 'circle(50% at 50% 50%)',
            }}>
              <div style={{
                position: 'absolute', top: 0, bottom: 0, width: '30%',
                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)',
                mixBlendMode: 'screen',
                animation: 'nerdai-shimmer 2.6s ease-in-out 0.3s infinite',
              }} />
            </div>
          </button>

          {/* Notification badge — outside overflow:hidden button so it's never clipped */}
          {totalNotifications > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs rounded-full flex items-center justify-center font-bold animate-bounce" style={{ zIndex: 1 }}>
              {totalNotifications}
            </span>
          )}
        </div>
      )}

      {/* Widget Panel - Mobile optimized */}
      {isOpen && (
        <>
          {/* Transparent backdrop — blocks all background interactions while chat is open */}
          <div className="fixed inset-0 z-40" aria-hidden="true" />

          <div id="chat-widget-panel" className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-96 sm:max-w-[calc(100vw-3rem)] h-[85vh] sm:h-[500px] sm:max-h-[70vh] bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl border border-amber-400 flex flex-col overflow-hidden">
          {/* Header - Amber/Gold theme */}
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-300 p-3 sm:p-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MessageCircle className="h-5 w-5 text-amber-600" />
              <span className="font-semibold text-amber-900 text-sm sm:text-base">Friends & Chat</span>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-amber-100 rounded-full p-1.5 text-amber-700">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs - Mobile optimized */}
          <div className="flex border-b border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50">
            <button
              onClick={() => setActiveTab('friends')}
              className={`flex-1 py-2 sm:py-2.5 text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                activeTab === 'friends' 
                  ? 'text-amber-900 border-b-2 border-amber-500 bg-amber-100/50' 
                  : 'text-amber-700 hover:text-amber-900 hover:bg-amber-100/30'
              }`}
            >
              <Users className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="whitespace-nowrap">Friends</span>
              <span className="text-[10px] whitespace-nowrap">({friends.length})</span>
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex-1 py-2 sm:py-2.5 text-xs font-medium transition-colors relative flex items-center justify-center gap-1 ${
                activeTab === 'requests' 
                  ? 'text-amber-900 border-b-2 border-amber-500 bg-amber-100/50' 
                  : 'text-amber-700 hover:text-amber-900 hover:bg-amber-100/30'
              }`}
            >
              <Bell className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="whitespace-nowrap">Requests</span>
              {friendRequests.length > 0 && (
                <Badge className="bg-orange-500 text-white text-[10px] px-1 py-0 min-w-[16px] flex-shrink-0">
                  {friendRequests.length}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setActiveTab('discover')}
              className={`flex-1 py-2 sm:py-2.5 text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                activeTab === 'discover' 
                  ? 'text-amber-900 border-b-2 border-amber-500 bg-amber-100/50' 
                  : 'text-amber-700 hover:text-amber-900 hover:bg-amber-100/30'
              }`}
            >
              <Search className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="whitespace-nowrap">Discover</span>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Friends Tab */}
            {activeTab === 'friends' && (
              <div className="p-3">
                {/* For Admin: Support Contacts (people they contacted for support) */}
                {isAdminOrSubadmin && directContacts.filter(c => c.display_category === 'support').length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-blue-600 font-medium mb-2 px-1">Support Contacts</p>
                    <div className="space-y-2">
                      {directContacts.filter(c => c.display_category === 'support').map((contact) => {
                        const contactUnread = unreadPerFriend[contact.other_user?.id] || 0;
                        return (
                          <div
                            key={contact.id}
                            onClick={() => openDirectContactChat(contact)}
                            className={`flex items-center space-x-3 p-3 rounded-xl hover:bg-blue-50 cursor-pointer transition-colors border ${contactUnread > 0 ? 'border-blue-300 bg-blue-50' : 'border-blue-100 hover:border-blue-200'}`}
                          >
                            <div className="relative">
                              <Avatar className="w-10 h-10">
                                {contact.other_user?.avatar_url ? (
                                  <img src={contact.other_user.avatar_url} alt="" className="w-full h-full object-cover rounded-full" loading="lazy" decoding="async"/>
                                ) : (
                                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white text-sm">
                                    {contact.other_user?.name?.charAt(0).toUpperCase() || 'U'}
                                  </AvatarFallback>
                                )}
                              </Avatar>
                              {contactUnread > 0 && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                                  {contactUnread > 9 ? '9+' : contactUnread}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium truncate text-sm ${contactUnread > 0 ? 'text-blue-900' : 'text-gray-900'}`}>
                                {contact.other_user?.name || 'Unknown'}
                              </p>
                              <p className={`text-xs ${contactUnread > 0 ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>
                                {contactUnread > 0 ? `${contactUnread} new message${contactUnread > 1 ? 's' : ''}` : 'Support contact'}
                              </p>
                            </div>
                            <MessageCircle className={`h-4 w-4 ${contactUnread > 0 ? 'text-blue-600' : 'text-blue-500'}`} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* For Regular Users: Support Team Section (messages from admin/subadmin) */}
                {!isAdminOrSubadmin && directContacts.filter(c => c.display_category === 'support_team').length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-blue-600 font-medium mb-2 px-1">Support Team</p>
                    <div className="space-y-2">
                      {directContacts.filter(c => c.display_category === 'support_team').map((contact) => {
                        const contactUnread = unreadPerFriend[contact.other_user?.id] || 0;
                        return (
                          <div
                            key={contact.id}
                            onClick={() => openDirectContactChat({
                              ...contact,
                              is_support_team: true,
                              other_user: { ...contact.other_user, name: 'Nerd AI Support Team' }
                            })}
                            className={`flex items-center space-x-3 p-3 rounded-xl hover:bg-blue-50 cursor-pointer transition-colors border ${contactUnread > 0 ? 'border-blue-300 bg-blue-50' : 'border-blue-100 hover:border-blue-200'}`}
                          >
                            <div className="relative">
                              <Avatar className="w-10 h-10">
                                <img 
                                  src="https://customer-assets.emergentagent.com/job_736cdda7-e974-4fad-aa62-3a758e69e883/artifacts/hc6xcw6c_Time%282%29.png" 
                                  alt="Support Team" 
                                  className="w-full h-full object-cover rounded-full" loading="lazy" decoding="async"/>
                              </Avatar>
                              {contactUnread > 0 && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                                  {contactUnread > 9 ? '9+' : contactUnread}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium truncate text-sm ${contactUnread > 0 ? 'text-blue-900' : 'text-gray-900'}`}>
                                Nerd AI Support Team
                              </p>
                              <p className={`text-xs ${contactUnread > 0 ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>
                                {contactUnread > 0 ? `${contactUnread} new message${contactUnread > 1 ? 's' : ''}` : 'Portal support'}
                              </p>
                            </div>
                            <MessageCircle className={`h-4 w-4 ${contactUnread > 0 ? 'text-blue-600' : 'text-blue-500'}`} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Job Contacts Section (for both admin and regular users) */}
                {directContacts.filter(c => {
                  if (isAdminOrSubadmin) {
                    // For admin: show contacts where type is 'job' or 'unknown' or 'incoming'
                    return c.display_category === 'job' || c.display_category === 'unknown' || c.display_category === 'incoming';
                  } else {
                    // For regular users: show non-support contacts (employers)
                    return c.display_category !== 'support_team';
                  }
                }).length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-purple-600 font-medium mb-2 px-1">Job Contacts</p>
                    <div className="space-y-2">
                      {directContacts.filter(c => {
                        if (isAdminOrSubadmin) {
                          return c.display_category === 'job' || c.display_category === 'unknown' || c.display_category === 'incoming';
                        } else {
                          return c.display_category !== 'support_team';
                        }
                      }).map((contact) => {
                        const contactUnread = unreadPerFriend[contact.other_user?.id] || 0;
                        return (
                          <div
                            key={contact.id}
                            onClick={() => openDirectContactChat(contact)}
                            className={`flex items-center space-x-3 p-3 rounded-xl hover:bg-purple-50 cursor-pointer transition-colors border ${contactUnread > 0 ? 'border-purple-300 bg-purple-50' : 'border-purple-100 hover:border-purple-200'}`}
                          >
                            <div className="relative">
                              <Avatar className="w-10 h-10">
                                {contact.other_user?.avatar_url ? (
                                  <img src={contact.other_user.avatar_url} alt="" className="w-full h-full object-cover rounded-full" loading="lazy" decoding="async"/>
                                ) : (
                                  <AvatarFallback className="bg-gradient-to-br from-purple-500 to-indigo-500 text-white text-sm">
                                    {contact.other_user?.name?.charAt(0).toUpperCase() || 'U'}
                                  </AvatarFallback>
                                )}
                              </Avatar>
                              {contactUnread > 0 && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                                  {contactUnread > 9 ? '9+' : contactUnread}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium truncate text-sm ${contactUnread > 0 ? 'text-purple-900' : 'text-gray-900'}`}>
                                {contact.other_user?.name || 'Unknown'}
                              </p>
                              <p className={`text-xs ${contactUnread > 0 ? 'text-purple-700 font-medium' : 'text-gray-500'}`}>
                                {contactUnread > 0 ? `${contactUnread} new message${contactUnread > 1 ? 's' : ''}` : 'Job contact'}
                              </p>
                            </div>
                            <MessageCircle className={`h-4 w-4 ${contactUnread > 0 ? 'text-purple-600' : 'text-purple-500'}`} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(directContacts.length > 0 && friends.length > 0) && (
                  <div className="border-t border-gray-200 mt-3 pt-2 mb-2">
                    <p className="text-xs text-gray-500 font-medium px-1">Friends</p>
                  </div>
                )}

                {friends.length === 0 && directContacts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No friends yet</p>
                    <Button
                      onClick={() => setActiveTab('discover')}
                      size="sm"
                      className="mt-3 bg-gradient-to-r from-blue-700 to-blue-800"
                    >
                      Find Friends
                    </Button>
                  </div>
                ) : friends.length > 0 ? (
                  <div className="space-y-2">
                    {friends.map((friendship) => {
                      const friendUnread = unreadPerFriend[friendship.friend?.id] || 0;
                      return (
                        <div
                          key={friendship.id}
                          onClick={() => openChat(friendship)}
                          className={`flex items-center space-x-3 p-3 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors border ${friendUnread > 0 ? 'border-amber-300 bg-amber-50' : 'border-transparent hover:border-gray-200'}`}
                        >
                          <div className="relative">
                            <Avatar className="w-10 h-10">
                              {friendship.friend?.avatar_url ? (
                                <img src={friendship.friend.avatar_url} alt="" className="w-full h-full object-cover rounded-full" loading="lazy" decoding="async"/>
                              ) : (
                                <AvatarFallback className="bg-gradient-to-br from-amber-500 to-yellow-500 text-white text-sm">
                                  {friendship.friend?.name?.charAt(0).toUpperCase() || 'U'}
                                </AvatarFallback>
                              )}
                            </Avatar>
                            {friendUnread > 0 && (
                              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                                {friendUnread > 9 ? '9+' : friendUnread}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium truncate text-sm ${friendUnread > 0 ? 'text-amber-900' : 'text-gray-900'}`}>
                              {friendship.friend?.name || 'Unknown'}
                            </p>
                            <p className={`text-xs ${friendUnread > 0 ? 'text-amber-700 font-medium' : 'text-gray-500'}`}>
                              {friendUnread > 0 ? `${friendUnread} new message${friendUnread > 1 ? 's' : ''}` : 'Tap to chat'}
                            </p>
                          </div>
                          <MessageCircle className={`h-4 w-4 ${friendUnread > 0 ? 'text-amber-600' : 'text-amber-500'}`} />
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            )}

            {/* Requests Tab */}
            {activeTab === 'requests' && (
              <div className="p-3">
                {/* Show All Toggle */}
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                  <span className="text-xs text-gray-500">
                    {showAllRequests ? 'Showing all requests' : 'Showing pending only'}
                  </span>
                  <button
                    onClick={() => {
                      setShowAllRequests(!showAllRequests);
                      if (!showAllRequests) loadAllRequests();
                    }}
                    className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                  >
                    {showAllRequests ? 'Show Pending Only' : 'Show All Requests'}
                  </button>
                </div>

                {/* Pending Requests */}
                {!showAllRequests && (
                  <>
                    {friendRequests.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <div className="relative inline-block mb-2">
                          <Bell className="h-10 w-10 text-gray-300" />
                          <Coins className="h-4 w-4 text-yellow-500 absolute -top-1 -right-1" />
                        </div>
                        <p className="text-sm">No pending requests</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {friendRequests.map((request) => (
                          <div key={request.id} className="p-3 rounded-xl bg-gray-50 border border-gray-200">
                            <div className="flex items-center space-x-3 mb-3">
                              <Avatar className="w-10 h-10">
                                {request.sender?.avatar_url ? (
                                  <img src={request.sender.avatar_url} alt="" className="w-full h-full object-cover rounded-full" loading="lazy" decoding="async"/>
                                ) : (
                                  <AvatarFallback className="bg-gradient-to-br from-blue-700 to-blue-800 text-white text-sm">
                                    {request.sender?.name?.charAt(0).toUpperCase() || 'U'}
                                  </AvatarFallback>
                                )}
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate text-sm">
                                  {request.sender?.name || 'Unknown'}
                                </p>
                                <p className="text-xs text-gray-500">{formatTime(request.created_at)}</p>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <Button
                                onClick={() => handleAcceptRequest(request.id, request.sender_id, request.sender?.name)}
                                size="sm"
                                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-xs shadow-md"
                              >
                                <Coins className="h-3 w-3 mr-1 text-yellow-300" />
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Accept
                              </Button>
                              <Button
                                onClick={() => handleRejectRequest(request.id)}
                                variant="outline"
                                size="sm"
                                className="flex-1 text-xs border-red-200 text-red-600 hover:bg-red-50"
                              >
                                <X className="h-3 w-3 mr-1" />
                                Decline
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* All Requests View */}
                {showAllRequests && (
                  <div className="space-y-4">
                    {/* Received Requests */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 mb-2">Received ({allRequests.received.length})</h4>
                      {allRequests.received.length === 0 ? (
                        <p className="text-xs text-gray-400">No received requests</p>
                      ) : (
                        <div className="space-y-2">
                          {allRequests.received.map((request) => (
                            <div key={request.id} className={`p-2 rounded-lg border text-xs ${
                              request.status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
                              request.status === 'accepted' ? 'bg-green-50 border-green-200' :
                              'bg-red-50 border-red-200'
                            }`}>
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{request.sender?.name || 'Unknown'}</span>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                                  request.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                                  request.status === 'accepted' ? 'bg-green-200 text-green-800' :
                                  'bg-red-200 text-red-800'
                                }`}>
                                  {request.status}
                                </span>
                              </div>
                              <p className="text-gray-500 mt-1">{formatTime(request.created_at)}</p>
                              {/* Accept/Decline buttons for pending requests */}
                              {request.status === 'pending' && (
                                <div className="flex space-x-2 mt-2">
                                  <Button
                                    onClick={() => {
                                      handleAcceptRequest(request.id, request.sender_id, request.sender?.name);
                                      loadAllRequests();
                                    }}
                                    size="sm"
                                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-[10px] h-7"
                                  >
                                    <Coins className="h-2.5 w-2.5 mr-1 text-yellow-300" />
                                    <CheckCircle className="h-2.5 w-2.5 mr-1" />
                                    Accept
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      handleRejectRequest(request.id);
                                      loadAllRequests();
                                    }}
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 text-[10px] border-red-200 text-red-600 hover:bg-red-50 h-7"
                                  >
                                    <X className="h-3 w-3 mr-1" />
                                    Decline
                                  </Button>
                                </div>
                              )}
                              {/* Reset button for non-pending requests */}
                              {request.status !== 'pending' && (
                                <button
                                  onClick={() => handleResetRequest(request.id)}
                                  className="mt-2 text-amber-600 hover:text-amber-700 text-[10px] font-medium"
                                >
                                  Reset to Pending
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Sent Requests */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 mb-2">Sent ({allRequests.sent.length})</h4>
                      {allRequests.sent.length === 0 ? (
                        <p className="text-xs text-gray-400">No sent requests</p>
                      ) : (
                        <div className="space-y-2">
                          {allRequests.sent.map((request) => (
                            <div key={request.id} className={`p-2 rounded-lg border text-xs ${
                              request.status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
                              request.status === 'accepted' ? 'bg-green-50 border-green-200' :
                              'bg-red-50 border-red-200'
                            }`}>
                              <div className="flex items-center justify-between">
                                <span className="font-medium">To: {request.receiver?.name || 'Unknown'}</span>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                                  request.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                                  request.status === 'accepted' ? 'bg-green-200 text-green-800' :
                                  'bg-red-200 text-red-800'
                                }`}>
                                  {request.status}
                                </span>
                              </div>
                              <p className="text-gray-500 mt-1">{formatTime(request.created_at)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Discover Tab */}
            {activeTab === 'discover' && (
              <div className="p-3">
                <div className="relative mb-3">
                  <Search className="h-4 w-4 text-gray-400 absolute left-3 top-2.5" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Name, email, mobile, college..."
                    className="pl-9 text-sm h-9"
                  />
                </div>

                {filteredDiscovered.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="relative inline-block mb-2">
                      <Search className="h-10 w-10 text-gray-300" />
                      <Coins className="h-4 w-4 text-yellow-500 absolute -top-1 -right-1" />
                    </div>
                    <p className="text-sm">No users found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredDiscovered.slice(0, 10).map((discoveredUser) => (
                      <div key={discoveredUser.id} className="flex items-center space-x-3 p-3 rounded-xl bg-gray-50 border border-gray-200">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-gradient-to-br from-blue-700 to-blue-800 text-white text-sm">
                            {discoveredUser.full_name?.charAt(0).toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate text-sm">
                            {discoveredUser.full_name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {discoveredUser.college_name}
                          </p>
                        </div>
                        {isAdminOrSubadmin ? (
                          <Button
                            onClick={() => handleAdminDirectMessage(discoveredUser)}
                            size="sm"
                            disabled={loading}
                            className="bg-gradient-to-r from-purple-500 to-indigo-500 text-xs px-2"
                          >
                            <MessageCircle className="h-3 w-3 mr-1" />
                            Message
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleSendFriendRequest(discoveredUser.id, discoveredUser.full_name)}
                            size="sm"
                            disabled={sendingRequest[discoveredUser.id]}
                            className="bg-gradient-to-r from-blue-700 to-blue-800 text-xs px-2 shadow-md"
                          >
                            {sendingRequest[discoveredUser.id] ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                            ) : (
                              <>
                                <Coins className="h-3 w-3 mr-1 text-yellow-300" />
                                <UserPlus className="h-3 w-3" />
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        </>
      )}
    </>
  );
};

export default FloatingChatWidget;
